// Material Design 3 Theme for Pfadfinder Wutöschingen
// Forest-inspired color palette for scouts

import { createTheme } from '@mui/material/styles';

// Pfadfinder Scouting Colors - Nature-inspired palette
const pfadfinderColors = {
    // Light Palette
    light: {
        primary: {
            main: 'rgb(var(--color-primary))',
            light: 'rgb(var(--color-primary-container))',
            dark: 'rgb(var(--color-primary-on-container))',
            contrastText: 'rgb(var(--color-primary-on))',
            container: 'rgb(var(--color-primary-container))',
            onContainer: 'rgb(var(--color-primary-on-container))',
        },
        secondary: {
            main: 'rgb(var(--color-secondary))',
            light: 'rgb(var(--color-secondary-container))',
            dark: 'rgb(var(--color-secondary-on-container))',
            contrastText: 'rgb(var(--color-secondary-on))',
            container: 'rgb(var(--color-secondary-container))',
            onContainer: 'rgb(var(--color-secondary-on-container))',
        },
        tertiary: {
            main: 'rgb(var(--color-tertiary))',
            light: 'rgb(var(--color-tertiary-container))',
            dark: 'rgb(var(--color-tertiary-on-container))',
            contrastText: 'rgb(var(--color-tertiary-on))',
            container: 'rgb(var(--color-tertiary-container))',
            onContainer: 'rgb(var(--color-tertiary-on-container))',
        },
        background: {
            default: 'rgb(var(--color-surface))',
            paper: 'rgb(var(--color-surface-container))',
            surfaceVariant: 'rgb(var(--color-surface-variant))',
        },
        text: {
            primary: 'rgb(var(--color-surface-on))',
            secondary: 'rgb(var(--color-surface-on-variant))',
        }
    },
    // Dark Palette
    dark: {
        primary: {
            main: 'rgb(var(--color-primary))',
            light: 'rgb(var(--color-primary-container))',
            dark: 'rgb(var(--color-primary-on-container))',
            contrastText: 'rgb(var(--color-primary-on))',
            container: 'rgb(var(--color-primary-container))',
            onContainer: 'rgb(var(--color-primary-on-container))',
        },
        secondary: {
            main: 'rgb(var(--color-secondary))',
            light: 'rgb(var(--color-secondary-container))',
            dark: 'rgb(var(--color-secondary-on-container))',
            contrastText: 'rgb(var(--color-secondary-on))',
            container: 'rgb(var(--color-secondary-container))',
            onContainer: 'rgb(var(--color-secondary-on-container))',
        },
        tertiary: {
            main: 'rgb(var(--color-tertiary))',
            light: 'rgb(var(--color-tertiary-container))',
            dark: 'rgb(var(--color-tertiary-on-container))',
            contrastText: 'rgb(var(--color-tertiary-on))',
            container: 'rgb(var(--color-tertiary-container))',
            onContainer: 'rgb(var(--color-tertiary-on-container))',
        },
        background: {
            default: 'rgb(var(--color-surface))',
            paper: 'rgb(var(--color-surface-container))',
            surfaceVariant: 'rgb(var(--color-surface-variant))',
        },
        text: {
            primary: 'rgb(var(--color-surface-on))',
            secondary: 'rgb(var(--color-surface-on-variant))',
        }
    }
};

export const getMd3Theme = (mode = 'light') => {
    const colors = pfadfinderColors[mode];

    return createTheme({
        palette: {
            mode,
            primary: colors.primary,
            secondary: colors.secondary,
            error: {
                main: 'rgb(var(--color-error))',
                light: 'rgb(var(--color-error-container))',
                dark: 'rgb(var(--color-error-on-container))',
                contrastText: 'rgb(var(--color-error-on))',
            },
            background: colors.background,
            text: colors.text,
            divider: 'rgba(var(--color-outline-variant), 0.5)',
        },

        typography: {
            fontFamily: '"Roboto", "Inter", "Helvetica Neue", Arial, sans-serif',
            h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2 },
            h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3 },
            h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4 },
            h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
            h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.5 },
            h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
            body1: { fontSize: '1rem', lineHeight: 1.6 },
            body2: { fontSize: '0.875rem', lineHeight: 1.5 },
            button: { textTransform: 'none', fontWeight: 600 },
        },

        shape: {
            borderRadius: 12,
        },

        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: 'rgb(var(--color-surface))',
                        color: 'rgb(var(--color-surface-on))',
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgb(var(--color-surface-on-variant) / 0.5)',
                            borderRadius: '4px',
                        },
                    },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        borderRadius: 100,
                        padding: '10px 24px',
                        fontWeight: 600,
                    },
                },
            },
            MuiCard: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: {
                        borderRadius: 16,
                        border: '1px solid',
                        borderColor: 'rgb(var(--color-outline-variant))',
                        backgroundColor: 'rgb(var(--color-surface-container-low))',
                        color: 'rgb(var(--color-surface-on))',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: 'rgb(var(--color-surface-container))',
                        color: 'rgb(var(--color-surface-on))',
                    },
                },
            },
            MuiTextField: {
                defaultProps: { variant: 'outlined', fullWidth: true },
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 12,
                        },
                    },
                },
            },
            MuiAppBar: {
                defaultProps: { elevation: 0, color: 'inherit' },
                styleOverrides: {
                    root: {
                        backgroundColor: 'rgb(var(--color-surface))',
                        borderBottom: '1px solid',
                        borderColor: 'rgb(var(--color-outline-variant))',
                        color: 'rgb(var(--color-surface-on))',
                    },
                },
            },
        },
    });
};

export default getMd3Theme;
